import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import classNames from 'classnames';
import { ButtonLoading, ControlledTooltip } from '@shared/components';
import { ReactNode } from 'react';
import { TIcon } from '../../types';
import { useFocusRestore } from '../../hooks';

export type TConfirmDialogState = {
  isOpen: boolean;
  title: string;
  content: ReactNode;
  buttonLabel?: string;
  buttonIcon?: TIcon;
  secondaryButtonLabel?: string;
  onConfirm: () => void | Promise<void>;
  danger?: boolean;
};

type TProps = {
  state?: TConfirmDialogState;
  setState: (state: TConfirmDialogState) => void;
};

const ConfirmDialog = ({ state, setState }: TProps) => {
  useFocusRestore(state?.isOpen ?? false);

  return (
    <Dialog
      open={state?.isOpen || false}
      onOpenChange={(e, data) => {
        e.stopPropagation();
        if (state) setState({ ...state, isOpen: data.open });
      }}
    >
      <DialogSurface>
        <DialogBody>
          <ControlledTooltip
            enable={Number(state?.title?.length) > 45}
            content={state?.title || ''}
            relationship="description"
            withArrow
          >
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
                </DialogTrigger>
              }
              className="flex"
            >
              <span className="truncate">
                {state?.title?.endsWith('?') ? state.title.slice(0, -1) : state?.title || ''}
              </span>
              {state?.title?.endsWith('?') && <span className="flex-shrink-0">?</span>}
            </DialogTitle>
          </ControlledTooltip>
          <DialogContent>{state?.content || ''}</DialogContent>
          <DialogActions>
            <ButtonLoading
              appearance="primary"
              icon={state?.buttonIcon}
              onClick={async (e) => {
                e.stopPropagation();
                await state?.onConfirm();
              }}
              className={classNames({ 'bg-red-700': state?.danger })}
            >
              {state?.buttonLabel || 'OK'}
            </ButtonLoading>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">{state?.secondaryButtonLabel || 'Cancel'}</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export { ConfirmDialog };
